<template>
  <div class="bg-dark min-h-screen font-sans" data-theme="dark">
    <navigation />
    <main>
      <RouterView />
    </main>

    <!-- CollaBear mascot -->
    <Transition name="scale">
      <div class="collabear" v-show="shouldShowCollabear(scrollYPos)" @click="showChat = true">
        <img class="collabear-img" @mouseover="showBubble = true"
          src="https://assets.br-gru-1.linodeobjects.com/landing-page/collabear.webp" alt="CollaBear" width="80"
          height="80" loading="lazy" />
        <div class="chat-bubble" v-show="shouldShowCollabearChat(scrollYPos)">
          <button class="close-chat-bubble" @click.stop="closeChatBubble" aria-label="Fechar chat">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <p class="text-sm text-gray-800">{{ collabearMsg }}</p>
        </div>
      </div>
    </Transition>

    <!-- Chat input -->
    <div class="response-bubble" v-show="showChat && shouldShowCollabearChat(scrollYPos)">
      <input class="w-full text-sm text-gray-800 outline-none bg-transparent" v-model="userInput"
        placeholder="digite aqui" @keyup.enter="sendMsg" />
    </div>

    <!-- Scroll to top button -->
    <Transition name="scale">
      <div class="btn-container" v-show="!showChat && scrollYPos > 60">
        <button @click="toTop"
          class="w-11 h-11 rounded-full bg-panel border border-white/10 flex items-center justify-center text-white hover:bg-brand hover:border-brand transition-all shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>
    </Transition>

    <Footer />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useHead } from '@vueuse/head'
import { useRoute } from 'vue-router'
import axios from 'axios'
import navigation from "./components/Navigation.vue"
import Footer from "./components/Footer.vue"

const route = useRoute()
const canonicalUrl = computed(() => `https://teamcollab.net${route.path}`)

useHead({
  htmlAttrs: { lang: 'pt-BR' },
  titleTemplate: '%s | TeamCollab',
  meta: [
    { name: 'description', content: 'A TeamCollab cria produtos SaaS que simplificam a gestão do seu negócio. Conheça o eBiz e nossas ferramentas para orçamentos, vendas e mais.' },
    { property: 'og:site_name', content: 'TeamCollab' },
    { property: 'og:type', content: 'website' },
    { property: 'og:locale', content: 'pt_BR' },
    { property: 'og:image', content: 'https://assets.br-gru-1.linodeobjects.com/landing-page/team_logo.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: 'https://assets.br-gru-1.linodeobjects.com/landing-page/team_logo.png' },
    { name: 'theme-color', content: '#030712' },
  ],
  link: [
    { rel: 'canonical', href: canonicalUrl },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'TeamCollab',
        url: 'https://teamcollab.net',
        logo: 'https://assets.br-gru-1.linodeobjects.com/landing-page/team_logo.png',
        description: 'Produtos SaaS prontos para simplificar a gestão e impulsionar os resultados do seu negócio.',
        telephone: '+55-31-99582-1453',
        email: 'contato@teamcollab.net',
        sameAs: [
          'https://www.linkedin.com/company/teamcollabspace/',
          'https://www.instagram.com/teamcollabnet',
          'https://www.facebook.com/people/TeamCollab/61578821924281/',
        ],
      }),
    },
  ],
})

const collabearMsg = ref("Olá sou CollaBear, em que posso ajudar?")
const showChat = ref(false)
const showBubble = ref(true)
const scrollYPos = ref(0)
const userInput = ref('')
let cachedContactThreshold = null

const shouldShowCollabear = (yPos) => {
  return cachedContactThreshold !== null && yPos > cachedContactThreshold
}

const shouldShowCollabearChat = (yPos) => {
  return cachedContactThreshold !== null && showBubble.value && yPos > cachedContactThreshold + 200
}

function recalcCollabearThreshold() {
  const contactEl = document.getElementById('contact')
  if (!contactEl) { cachedContactThreshold = null; return }
  const contactPosition = contactEl.getBoundingClientRect().top + window.scrollY
  cachedContactThreshold = Math.max(contactPosition - window.innerHeight, 500)
}

function toTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

function closeChatBubble() {
  showBubble.value = false
  showChat.value = false
}

function sendMsg() {
  axios.post("https://teamcollab.net/chatbot/collabear/prompt", {
    message: userInput.value,
  }).then((resp) => {
    collabearMsg.value = resp.data.response
  }).catch((e) => {
    console.error("error", e)
  })
  userInput.value = ""
}

onMounted(() => {
  let scrollTicking = false
  window.addEventListener('scroll', () => {
    if (!scrollTicking) {
      scrollTicking = true
      requestAnimationFrame(() => {
        scrollYPos.value = Math.round(window.pageYOffset)
        if (cachedContactThreshold === null) {
          recalcCollabearThreshold()
        }
        scrollTicking = false
      })
    }
  }, { passive: true })

  let resizeTimer = null
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
      recalcCollabearThreshold()
      scrollYPos.value = Math.round(window.pageYOffset)
    }, 200)
  }, { passive: true })
})
</script>

<style scoped>
.collabear {
  z-index: 99;
  border-radius: 20%;
  position: fixed;
  bottom: 16px;
  left: 16px;
}

.collabear-img {
  z-index: 99;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  position: absolute;
  bottom: 16px;
  left: 16px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.collabear-img:hover {
  transform: scale(1.1);
}

.chat-bubble {
  position: relative;
  background-color: #ffffff;
  border-radius: 10px;
  padding: 15px;
  max-width: 500px;
  left: 70px;
  bottom: 105px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

@media (max-width: 480px) {
  .chat-bubble {
    max-width: calc(100vw - 130px);
    left: 50px;
    padding: 12px;
    font-size: 14px;
  }
}

.chat-bubble::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 10px;
  width: 0;
  height: 0;
  border: 10px solid transparent;
  border-top-color: #ffffff;
  border-bottom: 0;
  border-left: 0;
  margin-left: -3px;
  margin-bottom: -10px;
}

.close-chat-bubble {
  background-color: #ffffff;
  color: #000000;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  top: -8px;
  right: -8px;
  position: absolute;
  border: 1px solid #e5e7eb;
  cursor: pointer;
}

.response-bubble {
  z-index: 99;
  margin-top: 10px;
  position: fixed;
  background-color: #ffffff;
  border-radius: 10px;
  padding: 15px;
  max-width: 500px;
  left: 135px;
  bottom: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

@media (max-width: 480px) {
  .response-bubble {
    max-width: calc(100vw - 130px);
    left: 110px;
    padding: 12px;
  }
}

.response-bubble::after {
  content: "";
  position: absolute;
  bottom: 0;
  right: 10px;
  width: 0;
  height: 0;
  border: 10px solid transparent;
  border-top-color: #ffffff;
  border-bottom: 0;
  border-right: 0;
  margin-right: -3px;
  margin-bottom: -10px;
}

.btn-container {
  z-index: 99;
  position: fixed;
  bottom: 16px;
  right: 16px;
}
</style>
